import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, Building, Calendar, FileText } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import { LEIRecord } from '../services/searchService';
import { formatLegalForm } from '../utils/legalFormUtils';

interface SearchResultsProps {
    records: LEIRecord[];
    total: number;
    page: number;
    size: number;
    onPageChange: (page: number) => void;
    loading: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({
    records,
    total,
    page,
    size,
    onPageChange,
    loading
}) => {
    const location = useLocation();
    const totalPages = Math.ceil(total / size);
    const startRecord = (page - 1) * size + 1;
    const endRecord = Math.min(page * size, total);

    // Create state to pass when navigating to record details
    const createRecordLinkState = () => ({
        fromSearch: true,
        searchQuery: location.search // This includes all the URL parameters
    });

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded-sm w-1/4 mb-4"></div>
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="border-b border-gray-100 py-4 last:border-b-0">
                            <div className="h-4 bg-gray-200 rounded-sm w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded-sm w-1/2 mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded-sm w-1/3"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (records.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria or filters.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-xs border border-gray-200">
            {/* Results Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">
                            Showing {startRecord} to {endRecord} of {total.toLocaleString()} results
                        </p>
                    </div>
                    <div className="text-sm text-gray-600">
                        Page {page} of {totalPages}
                    </div>
                </div>
            </div>

            {/* Results List */}
            <div className="divide-y divide-gray-100">
                {records.map((record) => (
                    <div key={record.lei} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1">
                                <div className="mb-2">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="text-lg font-medium text-gray-900">
                                            <Link
                                                to={`/record/${record.lei}`}
                                                state={createRecordLinkState()}
                                                className="hover:text-blue-600 transition-colors"
                                            >
                                                {record.registered_name}
                                            </Link>
                                        </h3>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {record.lei}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                                    {record.registered_country_code && (
                                        <div className="flex items-center gap-2">
                                            <ReactCountryFlag
                                                countryCode={record.registered_country_code}
                                                svg
                                                style={{
                                                    width: '16px',
                                                    height: '12px'
                                                }}
                                                title={record.registered_country_code}
                                            />
                                            <span>{record.registered_country_code}</span>
                                        </div>
                                    )}

                                    {record.legal_form && (
                                        <div className="flex items-center gap-2">
                                            <Building className="h-4 w-4 text-gray-400" />
                                            <span>{formatLegalForm(record.legal_form)}</span>
                                        </div>
                                    )}

                                    {record.lei_assignment_date && (
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                            <span>
                                                Registered:{' '}
                                                {new Date(
                                                    record.lei_assignment_date
                                                ).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}

                                    {record.lou && (
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-gray-400" />
                                            <span>LOU: {record.lou}</span>
                                        </div>
                                    )}

                                    {record.entity_status_code && (
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                                    record.entity_status_code === 'ACTIVE'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-700'
                                                }`}
                                            >
                                                Status: {record.entity_status_code}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-shrink-0">
                                <Link
                                    to={`/record/${record.lei}`}
                                    state={createRecordLinkState()}
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                >
                                    View Details
                                    <ExternalLink className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <button
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                                if (pageNum > totalPages) return null;

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => onPageChange(pageNum)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                            pageNum === page
                                                ? 'bg-blue-600 text-white'
                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <div>
                            <button
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= totalPages}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchResults;
